import React from 'react';
import pt from 'prop-types';
import { Snackbar as MuiSnackbar } from '@mui/material';

import withStyles from '@mui/styles/withStyles';

const SnackbarTypes = { default: 'default', error: 'error', success: 'success', validation: 'validation' };

// Per-type accent colour + soft glow + which glyph to draw. The card itself is
// the same dark-glass surface as the notification / dock popovers so a toast
// reads as part of the same design language on every screen.
const TOAST_THEME = {
  success: { accent: '#2fbf71', glow: 'rgba(47, 191, 113, 0.32)', icon: 'check' },
  error: { accent: '#ff5a5f', glow: 'rgba(255, 90, 95, 0.32)', icon: 'x' },
  validation: { accent: '#ffb020', glow: 'rgba(255, 176, 32, 0.32)', icon: 'alert' },
  default: { accent: '#5aa5ef', glow: 'rgba(90, 165, 239, 0.32)', icon: 'info' },
};

const ToastIcon = ({ kind, color }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 2.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  if (kind === 'check') return (<svg {...common}><path d="M20 6 9 17l-5-5" /></svg>);
  if (kind === 'x') {
    return (
      <svg {...common}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );
  }
  if (kind === 'alert') {
    return (
      <svg {...common}>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
};

ToastIcon.propTypes = { kind: pt.string.isRequired, color: pt.string.isRequired };

// Default: anchored top-centre and offset below the floating nav pill / header so
// a toast never collides with the top pill. zIndex kept very high so it floats
// over dialogs and the WebGL aurora alike.
const StyledSnackbar = withStyles(() => ({
  root: {
    zIndex: 45000000000,
    top: '84px !important',
  },
}))(MuiSnackbar);

// placement="bottom": bottom-centre instead, lifted clear of the flows that park
// a fixed action dock down there (gift-content's Add-your-media / Next pill sits
// at bottom: 18px and is ~48px tall).
const BottomSnackbar = withStyles(() => ({
  root: {
    zIndex: 45000000000,
    bottom: '96px !important',
  },
}))(MuiSnackbar);

// placement="bottom-edge": bottom-centre down near the viewport edge. Used by
// /setpassword, which parks nothing down there and whose card bottom sits right
// where the 96px slot would land — the toast would cover its own submit button.
const BottomEdgeSnackbar = withStyles(() => ({
  root: {
    zIndex: 45000000000,
    bottom: '28px !important',
  },
}))(MuiSnackbar);

const BOTTOM_ROOTS = { bottom: BottomSnackbar, 'bottom-edge': BottomEdgeSnackbar };

// placement defaults to 'bottom' HERE rather than via Snackbar.defaultProps:
// a destructuring default lives in the render body, so React Fast Refresh always
// picks it up on a hot update. Reassigning defaultProps is a module-level side
// effect that a hot update can skip, which makes the default look like it isn't
// applying until a full page reload.
export const Snackbar = ({ autoHideDuration, isVisible, message, onClose, placement = 'bottom', type }) => {
  const theme = TOAST_THEME[type] || TOAST_THEME.default;
  const atBottom = Boolean(BOTTOM_ROOTS[placement]);
  const Root = BOTTOM_ROOTS[placement] || StyledSnackbar;
  return (
    <Root
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      open={isVisible}
      anchorOrigin={{ vertical: atBottom ? 'bottom' : 'top', horizontal: 'center' }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '280px',
        maxWidth: 'min(440px, 92vw)',
        padding: '13px 18px 13px 14px',
        borderRadius: '14px',
        background: 'rgba(18, 20, 28, 0.94)',
        WebkitBackdropFilter: 'blur(22px)',
        backdropFilter: 'blur(22px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderLeft: `3px solid ${theme.accent}`,
        boxShadow: `0 18px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px ${theme.glow}`,
        color: '#f5f5f7',
        fontFamily: '"Montserrat", sans-serif',
      }}
      >
        <span style={{
          flex: '0 0 auto',
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.glow,
        }}
        >
          <ToastIcon kind={theme.icon} color={theme.accent} />
        </span>
        <span style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.35 }}>
          {message}
        </span>
      </div>
    </Root>
  );
};

Snackbar.defaultProps = {
  autoHideDuration: 4000,
  // placement's default is the destructuring default in the component signature
  // above — declaring it here too would be a second source of truth.
  type: SnackbarTypes.default,
};

Snackbar.propTypes = {
  autoHideDuration: pt.number,
  isVisible: pt.bool.isRequired,
  message: pt.string.isRequired,
  onClose: pt.func.isRequired,
  placement: pt.oneOf(['top', 'bottom', 'bottom-edge']),
  type: pt.oneOf(Object.values(SnackbarTypes)),
};
